import { Link, navigate } from "@/lib/router";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useEffect, useState } from "react";
import { Public, Me, getAuth, onAuthChange, Inbox } from "@/lib/api";
import { Search, FileText, ShieldCheck, User2, Menu, X, Award, Crown, Bell } from "lucide-react";
import logoUrl from "@/logo.svg";
import { AnnouncementMarquee, AnnouncementPopup, AnnouncementSidebar } from "@/components/Announcements";

export function Layout({ children }: { children: React.ReactNode }) {
  const [cats, setCats] = useState<any[]>([]);
  const [site, setSite] = useState<any>({});
  const [me, setMe] = useState<any>(null);
  const [q, setQ] = useState("");
  const [openMenu, setOpenMenu] = useState(false);
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    Public.categories().then(setCats).catch(() => {});
    Public.siteConfig().then(setSite).catch(() => {});
  }, []);

  // 同步登录状态：初始 + 订阅 setAuth 事件
  useEffect(() => {
    const refresh = () => {
      if (getAuth()) Me.profile().then(setMe).catch(() => setMe(null));
      else setMe(null);
    };
    refresh();
    return onAuthChange(refresh);
  }, []);

  // 站内信未读数：登录后定期刷新
  useEffect(() => {
    if (!me) { setUnread(0); return; }
    let alive = true;
    const tick = () => Inbox.unreadCount().then(r => { if (alive) setUnread(r.unread); }).catch(() => {});
    tick();
    const t = setInterval(tick, 60_000);
    return () => { alive = false; clearInterval(t); };
  }, [me?.id]);

  // 路由变化时自动收起移动抽屉
  useEffect(() => {
    const close = () => setOpenMenu(false);
    window.addEventListener("hashchange", close);
    return () => window.removeEventListener("hashchange", close);
  }, []);

  const onSearch = (e: React.FormEvent) => {
    e.preventDefault();
    navigate("/search", { q });
  };

  const siteName = site?.["site.basic"]?.name || "招投通";
  const topCats = cats.filter((c) => !c.parent_id).slice(0, 8);

  return (
    <div className="min-h-screen flex flex-col bg-background text-foreground">
      {/* Top bar */}
      <div className="border-b bg-card/50 backdrop-blur supports-[backdrop-filter]:bg-card/40 sticky top-0 z-40">
        <div className="container mx-auto px-4 h-14 md:h-16 flex items-center gap-3 md:gap-6">
          <Link to="/" className="flex items-center gap-2 font-bold text-base md:text-lg shrink-0">
            <img src={logoUrl} alt="logo" className="h-7 w-7 md:h-8 md:w-8 rounded-md shadow-sm" />
            <span className="truncate max-w-[7em] md:max-w-none">{siteName}</span>
          </Link>
          <nav className="hidden lg:flex items-center gap-4 text-sm text-muted-foreground">
            <Link to="/winners" className="hover:text-amber-600 transition-colors inline-flex items-center gap-1 font-medium">
              <Award className="h-3.5 w-3.5" /> 中标快讯
            </Link>
            {topCats.map((c) => (
              <Link key={c.id} to={`/c/${c.slug}`} className="hover:text-foreground transition-colors">
                {c.name}
              </Link>
            ))}
          </nav>
          <form onSubmit={onSearch} className="flex-1 max-w-md ml-auto hidden md:flex">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="搜索项目、招标方、中标人…"
                className="pl-9"
              />
            </div>
          </form>
          <div className="flex items-center gap-1 md:gap-2 ml-auto md:ml-0">
            {me ? (
              <>
                {(() => {
                  // 计算最高等级会员名片：取距今最远的过期时间作为代表
                  const ms: any[] = Array.isArray(me.memberships) ? me.memberships : [];
                  const active = ms.filter((m) => !m.expires_at || new Date(m.expires_at) > new Date());
                  if (active.length === 0) return null;
                  // 优先 lifetime（无 expires_at），再按 expires_at 取最远
                  const top = active.find((m) => !m.expires_at) ||
                    active.slice().sort((a, b) => new Date(b.expires_at).getTime() - new Date(a.expires_at).getTime())[0];
                  return (
                    <Link
                      to="/me"
                      title={top.expires_at ? `${top.name} · 到期 ${new Date(top.expires_at).toLocaleDateString()}` : `${top.name} · 永久`}
                      className="hidden sm:inline-flex items-center gap-1 rounded-full
                                 bg-gradient-to-r from-amber-400/90 to-orange-500/90
                                 text-white text-[11px] font-semibold px-2 py-0.5 shadow-sm
                                 hover:from-amber-500 hover:to-orange-600 transition-colors"
                    >
                      <Crown className="h-3 w-3" />
                      {top.name || "VIP"}
                    </Link>
                  );
                })()}
                <Link to="/me" className="text-sm hidden sm:flex items-center gap-1.5 hover:text-foreground text-muted-foreground max-w-[8em] md:max-w-none">
                  <User2 className="h-4 w-4" /> <span className="truncate">{me.display_name || me.email}</span>
                </Link>
                <Link to="/me" className="sm:hidden p-2 text-muted-foreground hover:text-foreground">
                  <User2 className="h-5 w-5" />
                </Link>
                <Link to="/me" query={{ tab: "inbox" }} className="relative p-2 text-muted-foreground hover:text-foreground" title={unread > 0 ? `${unread} 条未读站内信` : "站内信"}>
                  <Bell className="h-5 w-5" />
                  {unread > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center
                                     min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white
                                     text-[10px] leading-none font-semibold ring-2 ring-background
                                     shadow-sm animate-in fade-in zoom-in">
                      {unread > 99 ? "99+" : unread}
                    </span>
                  )}
                </Link>
                {me.roles?.includes("admin") && (
                  <Link to="/admin" className="text-sm hidden lg:flex items-center gap-1.5 hover:text-foreground text-muted-foreground">
                    <ShieldCheck className="h-4 w-4" /> 后台
                  </Link>
                )}
              </>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" size="sm" className="px-2 md:px-3">登录</Button></Link>
                <Link to="/register"><Button size="sm" className="px-2 md:px-3">注册</Button></Link>
              </>
            )}
            <button className="lg:hidden p-2" onClick={() => setOpenMenu((v) => !v)} aria-label="菜单">
              {openMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
        {openMenu && (
          <div className="lg:hidden border-t bg-card">
            <div className="px-4 py-3 space-y-3">
              {/* mobile search */}
              <form onSubmit={(e) => { onSearch(e); setOpenMenu(false); }} className="md:hidden">
                <div className="relative w-full">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="搜索关键词…" className="pl-9" />
                </div>
              </form>
              <div className="flex flex-wrap gap-x-4 gap-y-2 text-sm">
                <Link to="/winners" className="text-amber-600 font-medium inline-flex items-center gap-1">
                  <Award className="h-3.5 w-3.5" /> 中标快讯
                </Link>
                {topCats.map((c) => (
                  <Link key={c.id} to={`/c/${c.slug}`} className="text-muted-foreground hover:text-foreground">{c.name}</Link>
                ))}
              </div>
              {me?.roles?.includes("admin") && (
                <Link to="/admin" className="text-sm inline-flex items-center gap-1.5 text-muted-foreground">
                  <ShieldCheck className="h-4 w-4" /> 进入后台
                </Link>
              )}
            </div>
          </div>
        )}
      </div>

      <AnnouncementMarquee />

      <main className="flex-1">{children}</main>

      <AnnouncementPopup />
      <AnnouncementSidebar />

      <footer className="border-t mt-12">
        <div className="container mx-auto px-4 py-8 text-sm text-muted-foreground flex flex-col md:flex-row items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <FileText className="h-4 w-4" /> {site?.["site.basic"]?.copyright || `© ${new Date().getFullYear()} ${siteName}`}
          </div>
          <div className="flex gap-4">
            <Link to="/pricing" className="hover:text-foreground">会员套餐</Link>
            <a href="/sitemap.xml" className="hover:text-foreground">Sitemap</a>
            <a href="/rss.xml" className="hover:text-foreground">RSS</a>
            {site?.["site.basic"]?.icp && <span>{site["site.basic"].icp}</span>}
          </div>
        </div>
      </footer>
    </div>
  );
}
